/**
 * club_c7.js — Commerce7 wine-club membership, common feature (any C7 tenant).
 *
 * Currently only active for tenants in configAccessors.js's isMembershipEnabled()
 * allowlist (a plain constant for now — see that file's comment; not yet a real
 * tenant-config flag). The flow itself is tenant-agnostic.
 *
 * PARITY / DEVIATIONS FROM OLD CODE (RedConcierge/static/javascript/main.js,
 * CHECKOUT.membership + createWineClubSelection + related club functions):
 *   - Old join was chat/LLM-driven (send "Signup for {club}", let the backend
 *     NLU infer intent) and the logged-in in-widget path was never actually
 *     wired up (dead code) — renderWineClubSelection didn't exist, so the
 *     "wine_club_selection" response silently threw. This is a clean,
 *     deterministic rebuild using the already-built REST endpoints instead.
 *   - The marketing MembershipCard feed (LLM-curated club blurbs) has no real
 *     Commerce7 club id, so Join resolves the real club by matching the
 *     card's title against the live c7GetClubs() list.
 *   - No frequency picker during initial join (old didn't have one either —
 *     defaultFrequency is server-set at signup time). Frequency is only
 *     user-editable later, via "manage my membership" (see below).
 *   - Club payment currently only supports an existing saved card. C7's
 *     "add card to customer" endpoint (used for a brand-new card during
 *     signup) requires a tokenized card (maskedCardNumber/tokenOnFile), not a
 *     raw card number — unlike the regular checkout charge endpoint, which
 *     does accept raw card fields. No client-side tokenizer is wired up, so a
 *     brand-new card can't be safely supported yet. (Old code had the same
 *     effective gap — its guest/new-card club path silently hit the wrong,
 *     cart-only endpoint.) Flagged as a known follow-up, not silently faked.
 *
 * FLOW (new join):
 *   MembershipCard "Join Now" (logged in) → ws-club-join {clubId}
 *     → already an ACTIVE membership for this exact clubId (c7GetMemberships)?
 *       → skip straight to membership-details {membership} (old parity:
 *         CHECKOUT.membership.showMembershipDetails() — no new signup, no
 *         wine-selection carousel; edit flows are the existing
 *         showMembership()/MembershipEditForm.js ones, unchanged)
 *     → otherwise: resolve real club (c7GetClubs) → c7CreateSignup → c7GetSignupProducts
 *       → club-wine-selection {club, items, minimumBottles}
 *         → ws-club-items-changed {items}  (synced via c7UpdateSignupItems
 *           once the minimum is reached, old parity)
 *         → "Continue" → ws-club-review-order → checkout-options (Ship/Pickup,
 *           reused base component) — collect delivery details BEFORE showing
 *           totals, so the summary's Ship to/Pickup by block is populated.
 *             → ws-club-checkout-method {type}
 *               ship (logged in, has saved addresses)
 *                 → checkout-address-options (ShipAddressOptions, same picker
 *                   the cart checkout uses)
 *                   → ws-club-address-select {address} → handleAddressSubmit
 *                   → ws-club-address-new {}            → checkout-form {type:"ship"}
 *                   → ws-club-address-edit {address}    → checkout-address-edit-form
 *                     → ws-club-address-update {addressId, payload} → re-show picker
 *               → checkout-form {type}  (the tenant's own CheckoutForm.js — same
 *                 ship/pickup UI as the cart checkout: full address fields +
 *                 country/province dropdowns for ship, name + pickup-location
 *                 list for pickup)
 *                 → ws-club-checkout-submit {type, fields} → handleAddressSubmit
 *                   → c7GetSignupVirtualCart
 *                     → club-order-review (wines + totals, now address-aware)
 *                       → ws-club-continue-checkout → club-checkout-form {type:"payment"}
 *                         → ws-club-payment-submit {cardId} (saved card) OR
 *                           {card} (Stripe-tokenized new card, when this
 *                           tenant has a Stripe key configured — see
 *                           ClubCheckoutForm.js / stripe_c7.js) → c7SignupCheckout
 *                           → club-confirmation {orderId} + clear session
 *
 *   All the ship/pickup + saved-address events above use their own
 *   "ws-club-*" names (rather than the shared defaults ShipAddressOptions.js/
 *   CheckoutForm.js/ShipAddressEditForm.js emit) so this club flow never also
 *   fires checkout_c7.js's cart-checkout listeners — those have no session
 *   guard of their own and would proceed the wrong (cart, not club-signup)
 *   checkout if they saw the same event.
 *
 *   club-order-review's "Ship to"/"Pickup by" Edit → ws-club-checkout-edit
 *     → BOTH checkout-options (Ship/Pickup choice, in case the customer wants
 *       to switch delivery type) AND the current type's checkout-form,
 *       prefilled from session.address, so an in-place edit needs no extra
 *       click.
 *       → ws-club-checkout-method {type} (picking Ship/Pickup from the
 *         choice) → same path as the first time through — prefilled from
 *         session.address again if it's the same type re-picked, blank if
 *         switching to the other one.
 *
 * FLOW (manage an existing membership):
 *   "__view_membership__" prompt → c7GetMemberships → membership-details
 *     → edit buttons → membership-edit-form {type: delivery|payment|frequency|cancel}
 *       → c7UpdateMembership / c7CancelMembership → re-show membership-details
 *
 * EXPORTS: init() — idempotent.
 */

import * as api from "../../core/api.js";
import { on, emit, getState, addMessage } from "../../core/state.js";
import { getTenantId } from "../../core/configAccessors.js";
import { error } from "../../core/env.js";

function getToken() {
  return getState().user?.customerToken || null;
}

function getCustomerId() {
  return getState().user?.customerId || "";
}

function showText(text) {
  addMessage({ role: "bot", kind: "text", data: { text } });
}

function genericError(e, fallback) {
  error("[club_c7] request failed", e);
  showText((e && e.message) ? e.message : fallback);
}

// Session state for an in-progress join (one at a time — matches old
// clubSignupId-cookie / single-active-signup behavior).
//
// PERSISTENCE: the chat transcript itself survives a reload (chat-history.js
// mirrors every message to localStorage), so a mid-signup checkout-form or
// club-order-review bubble re-renders fine — but every handler here guards on
// `if (!session) return`, and `session` was a plain in-memory variable, wiped
// by the reload. That left the re-rendered form's Continue button clicking
// into a silent no-op (session gone, handler bails). Mirrored here the same
// way cart_c7.js persists cartId — key ws_c7club_<tenantId>, whole small
// object (signupId/clubId/items/deliveryType/address), so a reload mid-signup
// resumes instead of stranding whatever step was on screen.
let session = null;

export const CLUB_SESSION_KEY = (tenantId) => `ws_c7club_${tenantId || "default"}`;

function loadStoredSession() {
  try {
    const raw = localStorage.getItem(CLUB_SESSION_KEY(getTenantId()));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Call after every session create/mutate/clear so the stored copy never
// drifts from the in-memory one.
function persistSession() {
  try {
    if (session) {
      localStorage.setItem(CLUB_SESSION_KEY(getTenantId()), JSON.stringify(session));
    } else {
      localStorage.removeItem(CLUB_SESSION_KEY(getTenantId()));
    }
  } catch {
    /* storage blocked — session stays in memory for this page only */
  }
}

function requireLogin() {
  if (getCustomerId() && getToken()) return true;
  emit("ws-account-open");
  return false;
}

// ─── Join a new club ─────────────────────────────────────────────────────

async function handleJoinClub({ clubId } = {}) {
  if (!requireLogin()) return;
  const token = getToken();
  const customerId = getCustomerId();

  emit("ws-chat-typing", { active: true });
  try {
    // Already an active member of THIS club? Skip the new-signup/wine-carousel
    // flow entirely and show the existing membership card instead — same
    // component/edit flows showMembership() already uses (MembershipDetails.js
    // + MembershipEditForm.js), old parity with CHECKOUT.membership.showMembershipDetails().
    // A membership for a DIFFERENT club doesn't short-circuit this — only an
    // exact clubId match does.
    const membershipsRes = await api.c7GetMemberships(customerId);
    const memberships = Array.isArray(membershipsRes?.clubMemberships) ? membershipsRes.clubMemberships : [];
    const existingMembership = memberships.find(
      (m) => m.club?.id === clubId && String(m.status || "").toLowerCase() === "active",
    );
    if (existingMembership) {
      emit("ws-chat-typing", { active: false });
      showText("You are already an active member of this club! Enjoy all your exclusive benefits and discounts.");
      addMessage({ role: "bot", kind: "membership-details", data: { membership: existingMembership } });
      return;
    }

    // The marketing card has no real club id — resolve it by title against
    // the live Commerce7 club list.
    const res = await api.c7GetClubs();
    const clubs = Array.isArray(res?.clubs) ? res.clubs : Array.isArray(res) ? res : [];
    const wanted = clubId;
    const club = clubs.find((c) => c.id === wanted) || clubs[0];
    if (!club) {
      emit("ws-chat-typing", { active: false });
      showText("Sorry, we couldn't find that club right now. Please try again.");
      return;
    }

    const signup = await api.c7CreateSignup({ clubId: club.id, customerId, customerToken: token });
    const signupId = signup?.id || signup?.signupId || "";
    if (!signupId) {
      emit("ws-chat-typing", { active: false });
      genericError(null, "We couldn't start your club signup. Please try again.");
      return;
    }

    const products = await api.c7GetSignupProducts({ signupId, customerToken: token });
    emit("ws-chat-typing", { active: false });
    const items = Array.isArray(products?.items)
      ? products.items
      : Array.isArray(products)
        ? products
        : [];
    const minimumBottles = Number(products?.minimumBottles) || 1;
    const defaultFrequency = products?.defaultFrequency ?? null;

    session = {
      signupId,
      clubId: club.id,
      clubTitle: club.title,
      minimumBottles,
      defaultFrequency,
      items: items.map((i) => ({ sku: i.sku, quantity: Number(i.quantity) || 0 })),
    };
    persistSession();

    showText(`Pick the wines for your ${club.title}.`);
    addMessage({
      role: "bot",
      kind: "club-wine-selection",
      data: { club, items, minimumBottles },
    });
  } catch (e) {
    emit("ws-chat-typing", { active: false });
    genericError(e, "We couldn't start your club signup. Please try again.");
  }
}

// ─── Wine selection ──────────────────────────────────────────────────────

// Old handleClubQtyChange: the signup's server-side item list is only synced
// once the minimum bottle count is reached — below minimum, changes stay local.
async function handleItemsChanged({ items } = {}) {
  if (!session) return;
  session.items = Array.isArray(items) ? items : [];
  persistSession();
  const total = session.items.reduce((n, it) => n + (Number(it.quantity) || 0), 0);
  if (total < session.minimumBottles) return;

  try {
    const res = await api.c7UpdateSignupItems({
      signupId: session.signupId,
      items: session.items.filter((it) => it.quantity > 0),
      customerToken: getToken(),
      clubId: session.clubId,
      frequency: session.defaultFrequency || undefined,
    });
    if (Array.isArray(res?.items)) {
      session.items = res.items.map((i) => ({ sku: i.sku, quantity: Number(i.quantity) || 0 }));
      persistSession();
    }
  } catch (e) {
    error("[club_c7] update signup items failed", e);
  }
}

// "Continue" (wine selection) — go straight to Ship/Pickup. The order
// summary now comes AFTER the address is collected (see handleAddressSubmit),
// so it can show the Ship to/Pickup by block instead of wines-only totals.
//
// eventName: "ws-club-checkout-method" — the shared CheckoutOptions component
// defaults to emitting "ws-checkout-method", which checkout_c7.js's reviewed
// flow (the regular cart checkout, also active for Valle) listens for
// unconditionally. Using the default here would fire BOTH handlers off one
// Ship/Pickup click and start the wrong (cart) checkout alongside this one.
// question: override for the bubble text — handleChangeDelivery passes
// "Change delivery type?" when re-showing this from the Edit pencil, since
// the default first-ask wording doesn't fit an already-delivered-type order.
function showDeliveryChoice(question) {
  addMessage({
    role: "bot",
    kind: "checkout-options",
    data: { eventName: "ws-club-checkout-method", question },
  });
}

function handleReviewOrder() {
  if (!session) return;
  showDeliveryChoice();
}

// ─── Checkout: address → order summary → payment → submit ──────────────
//
// Reuses the exact same shipping/pickup UI as the regular cart checkout
// (checkout_c7.js's "reviewed" flow) instead of a separate club-only form —
// one ship/pickup experience for cart orders, club orders, or any future
// membership flow. That means reusing "checkout-form" (the tenant's own
// CheckoutForm.js — full address fields + country/province dropdowns for
// ship, name + pickup-location list for pickup) and "checkout-address-options"
// (ShipAddressOptions.js, the saved-address picker). Both emit configurable
// event names specifically so this club flow doesn't also trigger
// checkout_c7.js's cart-checkout listeners, which have no session guard of
// their own and would proceed the wrong (cart, not club-signup) checkout.

const CLUB_ADDRESS_EVENTS = {
  select: "ws-club-address-select",
  new: "ws-club-address-new",
  edit: "ws-club-address-edit",
};

// Same lookup checkout_c7.js's getSavedAddresses does, duplicated here (not
// imported) to keep this module decoupled — same pattern as getToken/
// getCustomerId/showText above.
async function getSavedAddresses() {
  const token = getToken();
  const customerId = getCustomerId();
  if (!token || !customerId) return [];
  try {
    const res = await api.c7GetAddresses({ customerId, customerToken: token });
    return Array.isArray(res?.addresses) ? res.addresses : [];
  } catch (e) {
    error("[club_c7] saved addresses failed", e);
    return [];
  }
}

// A saved C7 address → the field shape CheckoutForm.js's own ship form
// submits (handleAddressSubmit/buildRawAddress below expect these keys).
function addressToFields(addr) {
  return {
    firstName: addr.firstName || "",
    lastName: addr.lastName || "",
    company: addr.company || "",
    address1: addr.address || "",
    address2: addr.address2 || "",
    city: addr.city || "",
    province: addr.stateCode || "",
    zip: addr.zipCode || "",
    country: addr.countryCode || "US",
    phone: addr.phone || "",
  };
}

function showAddressPicker(addresses) {
  showText("Please choose shipping address");
  addMessage({ role: "bot", kind: "checkout-address-options", data: { addresses, events: CLUB_ADDRESS_EVENTS } });
}

// prefillValues: the fields last submitted for this same delivery type (Edit
// pencil, re-picking the type already filled in) — same key shape the form
// itself submits (firstName/lastName/company/email/country/... — see
// addressToFields/buildRawAddress), so it can be handed straight back in.
// introText: a bot text bubble shown just above the form — only passed when
// this is that same-type re-edit, so "Update your pickup/shipping details."
// only appears alongside an already-filled-in form, not a blank first-time one.
function showAddressForm(type, prefillValues, introText) {
  if (introText) showText(introText);
  const data = { type, submitEvent: "ws-club-checkout-submit" };
  if (prefillValues) {
    data.values = { ...prefillValues };
    data.isEdit = true;
  } else if (type === "pickup" && getToken()) {
    // Same prefill checkout_c7.js's chooseMethod does for a logged-in pickup:
    // First/Last name from the customer profile, not treated as an Edit.
    const profile = getState().user?.profile || {};
    data.values = {
      firstName: profile.firstName || profile.first_name || "",
      lastName: profile.lastName || profile.last_name || "",
    };
  }
  addMessage({ role: "bot", kind: "checkout-form", data });
}

async function handleCheckoutMethod({ type } = {}) {
  if (!session || (type !== "ship" && type !== "pickup")) return;
  // Captured before overwriting deliveryType below — tells us whether this is
  // an Edit re-pick of the SAME type (prefill from what was already
  // submitted) or a switch to the other one (start that type's form blank).
  const previousType = session.deliveryType;
  session.deliveryType = type;
  persistSession();

  if (type === "ship") {
    const addresses = await getSavedAddresses();
    if (addresses.length) {
      showAddressPicker(addresses);
      return;
    }
  }
  const isSameTypeReEdit = type === previousType && !!session.address;
  showAddressForm(
    type,
    isSameTypeReEdit ? session.address : null,
    isSameTypeReEdit
      ? `Update your ${type === "pickup" ? "pickup" : "shipping"} details.`
      : null,
  );
}

// "Continue" on the saved-address picker — same core as a freshly typed form.
function handleAddressSelect({ address } = {}) {
  if (!session || !address) return;
  handleAddressSubmit({ type: "ship", fields: addressToFields(address) });
}

// "+ Add new address" on the picker → the blank ship form.
function handleAddressNew() {
  if (!session) return;
  showAddressForm("ship");
}

// Pencil icon on the picker → edit that saved address.
function handleAddressEdit({ address } = {}) {
  if (!session || !address) return;
  addMessage({
    role: "bot",
    kind: "checkout-address-edit-form",
    data: { address, updateEvent: "ws-club-address-update", errorScope: "club-address" },
  });
}

async function handleAddressUpdate({ addressId, payload } = {}) {
  if (!session || !addressId) return;
  const customerId = getCustomerId();
  const token = getToken();
  try {
    await api.c7UpdateAddress({ customerId, addressId, addressPayload: payload, customerToken: token || "" });
  } catch (e) {
    error("[club_c7] address update failed", e);
    emit("ws-checkout-error", { scope: "club-address", message: "We couldn't update that address. Please try again." });
    return;
  }
  const addresses = await getSavedAddresses();
  if (addresses.length) {
    showAddressPicker(addresses);
  } else {
    handleAddressNew();
  }
}

// Ship/pickup form submitted (or a saved address picked) — show the order
// summary (club-order-review) now that delivery details exist, instead of
// jumping straight to payment.
async function handleAddressSubmit({ type, fields = {} } = {}) {
  if (!session) return;
  session.deliveryType = type;
  session.address = fields;
  persistSession();
  const items = session.items.filter((it) => it.quantity > 0);
  emit("ws-chat-typing", { active: true });
  try {
    const cart = await api.c7GetSignupVirtualCart({
      signupId: session.signupId,
      clubId: session.clubId,
      items,
      customerToken: getToken(),
      deliveryPayload: buildPreviewDeliveryPayload(),
    });
    if (!cart) {
      emit("ws-chat-typing", { active: false });
      genericError(null, "We couldn't load your order summary. Please try again.");
      return;
    }
    // The virtual cart has no tender yet (payment is the step after this
    // review) — pull the customer's saved cards separately so the Payment
    // group can show the one that'll be charged, same data goToPaymentStep
    // uses for the payment step itself.
    let savedCards = [];
    try {
      const res = await api.c7GetSavedCards(getCustomerId());
      savedCards = Array.isArray(res?.savedCards) ? res.savedCards : [];
    } catch (e) {
      error("[club_c7] saved cards failed", e);
    }
    emit("ws-chat-typing", { active: false });
    showText("Please review your club order.");
    addMessage({
      role: "bot",
      kind: "club-order-review",
      data: { cart, clubTitle: session.clubTitle, type: session.deliveryType, savedCards },
    });
  } catch (e) {
    emit("ws-chat-typing", { active: false });
    genericError(e, "We couldn't load your order summary. Please try again.");
  }
}

// "Ship to"/"Pickup by" Edit on the order review → show BOTH the Ship/Pickup
// choice (in case the customer wants to switch delivery type) AND the
// already-chosen type's form, prefilled with what was submitted last time —
// so editing in place needs no extra click, but switching type is still one
// click away. Re-picking Ship/Pickup from the choice re-enters the normal
// handleCheckoutMethod path (saved-address picker for ship, the form
// directly for pickup), same as the first time through.
function handleChangeDelivery() {
  if (!session) return;
  showDeliveryChoice("Change delivery type?");
  if (session.deliveryType) {
    handleCheckoutMethod({ type: session.deliveryType });
  }
}

// Order review's "Continue..." — proceed to payment.
async function goToPaymentStep() {
  if (!session) return;
  // Payment step needs to know if there's a saved card to select from.
  emit("ws-chat-typing", { active: true });
  let savedCards = [];
  try {
    const res = await api.c7GetSavedCards(getCustomerId());
    savedCards = Array.isArray(res?.savedCards) ? res.savedCards : [];
  } catch (e) {
    error("[club_c7] saved cards failed", e);
  }
  emit("ws-chat-typing", { active: false });
  showText("Please select a payment method.");
  addMessage({ role: "bot", kind: "club-checkout-form", data: { type: "payment", savedCards } });
}

// Delivery info to set on the signup before fetching virtual-cart pricing,
// so the order-review card's "Ship to"/"Pickup by" isn't blank — Commerce7
// only resolves those in the virtual-cart response once a delivery method is
// set. Sent inline (not via _resolve_signup_delivery_and_card's saved-address
// flow) since this is just a pricing preview, not the final checkout — no
// address/card should be persisted here.
function buildPreviewDeliveryPayload() {
  const { deliveryType } = session;
  if (deliveryType === "pickup") return buildCheckoutPayload();
  if (deliveryType === "ship") {
    const shipTo = buildRawAddress();
    return { orderDeliveryMethod: "Ship", shipTo, billTo: shipTo };
  }
  return null;
}

// checkout_payload shape per backend's _resolve_signup_delivery_and_card —
// address is a raw NEW address (only when no saved address is selected).
function buildCheckoutPayload() {
  const { deliveryType, address = {} } = session;
  if (deliveryType === "pickup") {
    return { orderDeliveryMethod: "Pickup", pickupInventoryLocationId: address.inventoryLocationId || "" };
  }
  return { orderDeliveryMethod: "Ship" };
}

function buildRawAddress() {
  const a = session.address || {};
  return {
    firstName: a.firstName,
    lastName: a.lastName,
    company: a.company || "",
    phone: a.phone,
    address: a.address1,
    address2: a.address2 || "",
    city: a.city,
    stateCode: a.province,
    zipCode: a.zip,
    countryCode: a.country,
  };
}

// cardId: an existing saved card (unchanged path). card: a brand-new,
// Stripe-tokenized card (ClubCheckoutForm.js's "+ Add new card" — see its doc
// comment) — checkoutPayload omits customerCreditCardId so the backend's
// _resolve_signup_delivery_and_card saves it first (add_credit_card_to_customer)
// and fills the id in itself, same as it already does for a brand-new SHIP
// address (see buildRawAddress/the `address` param below).
async function handlePaymentSubmit({ cardId, card } = {}) {
  if (!session || (!cardId && !card)) return;
  emit("ws-chat-typing", { active: true });
  try {
    const checkoutPayload = cardId
      ? { ...buildCheckoutPayload(), customerCreditCardId: cardId }
      : buildCheckoutPayload();
    const res = await api.c7SignupCheckout({
      signupId: session.signupId,
      customerToken: getToken(),
      checkoutPayload,
      address: session.deliveryType === "ship" ? buildRawAddress() : undefined,
      card: card || undefined,
    });
    emit("ws-chat-typing", { active: false });
    const orderId = res?.orderNumber || res?.clubMembershipId || res?.id || "";
    if (!orderId) {
      genericError(null, "We couldn't complete your club signup. Please try again.");
      return;
    }
    addMessage({
      role: "bot",
      kind: "club-confirmation",
      data: { orderId, clubTitle: session.clubTitle, isMembership: !!res?.clubMembershipId },
    });
    session = null;
    persistSession();
  } catch (e) {
    emit("ws-chat-typing", { active: false });
    genericError(e, "We couldn't complete your club signup. Please try again.");
  }
}

// ─── Manage an existing membership ───────────────────────────────────────

async function showMembership() {
  const customerId = getCustomerId();
  if (!customerId) {
    emit("ws-account-open");
    return;
  }
  emit("ws-chat-typing", { active: true });
  try {
    const res = await api.c7GetMemberships(customerId);
    emit("ws-chat-typing", { active: false });
    const memberships = Array.isArray(res?.clubMemberships) ? res.clubMemberships : [];
    const active = memberships.find((m) => String(m.status || "").toLowerCase() === "active") || memberships[0];
    if (!active) {
      showText("You don't have an active club membership yet.");
      return;
    }
    addMessage({ role: "bot", kind: "membership-details", data: { membership: active } });
  } catch (e) {
    emit("ws-chat-typing", { active: false });
    genericError(e, "We couldn't load your membership. Please try again.");
  }
}

function handleMembershipEdit({ type, membership } = {}) {
  if (!type || !membership) return;
  addMessage({ role: "bot", kind: "membership-edit-form", data: { type, membership } });
}

async function handleMembershipUpdate({ membershipId, payload, membership } = {}) {
  if (!membershipId || !payload) return;
  emit("ws-chat-typing", { active: true });
  try {
    await api.c7UpdateMembership({ membershipId, payload });
    emit("ws-chat-typing", { active: false });
    showText("Your membership has been updated.");
    // Re-fetch so the summary reflects the change.
    await showMembership();
  } catch (e) {
    emit("ws-chat-typing", { active: false });
    genericError(e, "We couldn't update your membership. Please try again.");
  }
}

async function handleMembershipCancel({ membershipId, reason, comments } = {}) {
  if (!membershipId) return;
  emit("ws-chat-typing", { active: true });
  try {
    await api.c7CancelMembership({
      customerId: getCustomerId(),
      membershipId,
      cancellationReason: reason,
      cancellationComments: comments || "",
      customerToken: getToken(),
    });
    emit("ws-chat-typing", { active: false });
    showText("Your membership has been cancelled. We're sorry to see you go!");
  } catch (e) {
    emit("ws-chat-typing", { active: false });
    genericError(e, "We couldn't cancel your membership. Please try again.");
  }
}

let started = false;

export function init() {
  if (started) return;
  started = true;
  session = loadStoredSession();

  on("ws-club-join", handleJoinClub);
  on("ws-club-items-changed", handleItemsChanged);
  on("ws-club-review-order", handleReviewOrder);
  on("ws-club-continue-checkout", goToPaymentStep);
  on("ws-club-checkout-method", handleCheckoutMethod);
  on("ws-club-address-select", handleAddressSelect);
  on("ws-club-address-new", handleAddressNew);
  on("ws-club-address-edit", handleAddressEdit);
  on("ws-club-address-update", handleAddressUpdate);
  on("ws-club-checkout-submit", handleAddressSubmit);
  on("ws-club-checkout-edit", handleChangeDelivery);
  on("ws-club-payment-submit", handlePaymentSubmit);

  on("ws-prompt-select", ({ value } = {}) => {
    if (value === "__view_membership__") showMembership();
  });
  on("ws-membership-edit", handleMembershipEdit);
  on("ws-membership-update", handleMembershipUpdate);
  on("ws-membership-cancel", handleMembershipCancel);
}
